import { Person } from '@/api/profiles.model';
import { OptionItem } from '@/lib/types';
import { INDUSTRY_OPTIONS } from '@/mock/industryType';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ProfilesStore = {
  activeIndex: number;
  reviewed: number;
  profiles: Person[];
  dynamicIndustryList: OptionItem[];
  setProfiles: (profiles: Person[]) => void;
  resetProfiles: () => void;
  setActiveIndex: (index: number) => void;
  setReviewed: (reviewed: number) => void;
  setUpdated: (value: boolean) => void;
  updateProfileFields: (fields: Person['fields']) => void;
  resetIndustryList: (index: number) => void;
  addDynamicIndustry: (value: string) => void;
};

const useProfilesStore = create<ProfilesStore>()(
  persist(
    (set, get) => ({
      activeIndex: 0,
      reviewed: 0,
      profiles: [],
      dynamicIndustryList: [],
      setProfiles: (newProfiles) => {
        if (newProfiles) {
          const existingProfiles = get().profiles;

          // Create a Map of existing profiles using affinityId as the key for easy lookup
          const existingProfilesMap = new Map(
            existingProfiles.map((profile) => [profile.affinityId, profile]),
          );

          newProfiles.forEach((newProfile) => {
            const existingProfile = existingProfilesMap.get(newProfile.affinityId);

            // If the profile exists and `updated` is false, update its fields
            if (existingProfile && !existingProfile.updated) {
              Object.assign(existingProfile, newProfile);
            } else if (!existingProfile) {
              // If the profile does not exist, add it to the map
              existingProfilesMap.set(newProfile.affinityId, newProfile);
            }
          });

          const mergedProfiles = Array.from(existingProfilesMap.values());

          set({ profiles: mergedProfiles });
          get().resetIndustryList(get().activeIndex);
        }
      },
      resetProfiles: () => {
        set({ activeIndex: 0 });
        set({ profiles: [] });
      },
      setActiveIndex: (activeIndex) => {
        get().resetIndustryList(activeIndex);
        set({ activeIndex });
      },
      setReviewed: (reviewed) => {
        set({ reviewed });
      },
      setUpdated: (value) => {
        const { profiles, activeIndex } = get();

        const updatedProfiles = [...profiles];

        if (updatedProfiles[activeIndex]) {
          updatedProfiles[activeIndex].updated = value;
        }

        set({ profiles: updatedProfiles });
      },
      updateProfileFields: (fields) => {
        const { profiles, activeIndex } = get();
        const updatedProfiles = [...profiles];

        if (updatedProfiles[activeIndex]) {
          updatedProfiles[activeIndex].fields = {
            ...updatedProfiles[activeIndex].fields,
            ...fields,
          };
        }

        set({ profiles: updatedProfiles });
      },
      resetIndustryList: (index: number) => {
        const { profiles } = get();
        if (index === null || !profiles[index]) return;

        const currentProfile = profiles[index];
        const originalIndustryLabels = INDUSTRY_OPTIONS.map((opt) => opt.label.toLowerCase());
        const dynamicIndustries = new Set<string>();

        // Extract industry options from the current profile
        currentProfile.fields.industry.value.forEach((industry) => {
          if (!originalIndustryLabels.includes(industry.text.toLowerCase())) {
            dynamicIndustries.add(industry.text);
          }
        });

        // Create a merged list of original and dynamic options
        const dynamicIndustryList = [
          ...INDUSTRY_OPTIONS,
          ...Array.from(dynamicIndustries).map((text) => ({
            value: text,
            label: text,
          })),
        ];

        set({ dynamicIndustryList });
      },
      addDynamicIndustry: (newIndustry) => {
        const { dynamicIndustryList } = get();
        const existingLabels = dynamicIndustryList.map((opt) => opt.label.toLowerCase());

        // Check if the new industry already exists
        if (!existingLabels.includes(newIndustry.toLowerCase())) {
          const updatedDynamicIndustryList = [
            ...dynamicIndustryList,
            { value: newIndustry, label: newIndustry },
          ];
          set({ dynamicIndustryList: updatedDynamicIndustryList });
        }
      },
    }),
    {
      name: 'profile-storage',
    },
  ),
);

export default useProfilesStore;
